// Platform-admin auth context — SEPARATE from context/AuthContext.tsx.
//
// This context talks ONLY to /api/super_admin/auth/* (via platformApiFetch,
// which never shares state, cookies, or redirect targets with the tenant
// api/client.ts). It is mounted ONLY inside the /super_admin/* route subtree
// (see App.tsx's PlatformShell) — a tenant-agent page never mounts this
// provider and never triggers a call to /api/super_admin/auth/me.

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import {
  platformLogin,
  platformLogout,
  getPlatformMe,
  type PlatformAdminInfo,
} from '../api/platformAuth';

interface PlatformAuthState {
  admin: PlatformAdminInfo | null;
  loading: boolean;
}

interface PlatformAuthContextValue extends PlatformAuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
}

const PlatformAuthContext = createContext<PlatformAuthContextValue | null>(null);

export function PlatformAuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<PlatformAuthState>({
    admin: null,
    loading: true, // always start loading — check /me before rendering PlatformGuard
  });

  // On mount: call /me to restore session from the httpOnly platform_session
  // cookie. 401 (no session) is caught silently — PlatformGuard decides what
  // to render based on isAuthenticated, no side-effect navigation here.
  useEffect(() => {
    getPlatformMe()
      .then((admin) => setState({ admin, loading: false }))
      .catch(() => setState({ admin: null, loading: false }));
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    await platformLogin(email, password);
    const admin = await getPlatformMe();
    setState({ admin, loading: false });
  }, []);

  const logout = useCallback(async () => {
    try {
      await platformLogout();
    } catch {
      // If logout call fails (e.g. session already expired) we still clear locally.
    }
    setState({ admin: null, loading: false });
  }, []);

  return (
    <PlatformAuthContext.Provider
      value={{ ...state, isAuthenticated: !!state.admin, login, logout }}
    >
      {children}
    </PlatformAuthContext.Provider>
  );
}

export function usePlatformAuth(): PlatformAuthContextValue {
  const ctx = useContext(PlatformAuthContext);
  if (!ctx) throw new Error('usePlatformAuth must be used inside PlatformAuthProvider');
  return ctx;
}
